import { ApiService } from '../apiService'

/**
 * Offering an adventure to somebody else.
 *
 * An offer, not a push: an adventure carries obligations — people play it,
 * report problems with it, expect it to keep working — so nothing moves until
 * the recipient accepts. For a group, who may accept is the group's own
 * decision, governed by the AcceptAdventureTransfers rank permission.
 */

/** Matches the server's OwnerEntityType for the two cases that can receive one. */
export enum TransferTargetType {
  User = 0,
  Group = 1,
}

export enum AdventureTransferStatus {
  Pending = 0,
  Accepted = 1,
  Declined = 2,
  Withdrawn = 3,
  Expired = 4,
}

export interface AdventureTransferDto {
  id: string
  adventureId: string
  adventureName: string
  fromUserId: string
  /** Display name. The server never sends authentication usernames. */
  fromDisplayName: string
  toEntityType: TransferTargetType
  toEntityId: string
  toDisplayName: string
  message?: string | null
  status: AdventureTransferStatus
  createdDate: string
  /** When it lapses if nobody answers. */
  expiresAt: string
}

export const adventureTransfersApi = {
  offer: (
    adventureId: string,
    body: { toEntityType: TransferTargetType; toEntityId: string; message?: string }
  ) => ApiService.post<AdventureTransferDto>(`/api/adventures/${adventureId}/transfers`, body),

  /** Offers waiting for the caller, personally or through a group. */
  incoming: () =>
    ApiService.get<AdventureTransferDto[]>('/api/me/adventure-transfers/incoming'),

  /** Offers the caller has sent and not had answered. */
  outgoing: () =>
    ApiService.get<AdventureTransferDto[]>('/api/me/adventure-transfers/outgoing'),

  accept: (transferId: string) =>
    ApiService.post<void>(`/api/adventure-transfers/${transferId}/accept`),

  decline: (transferId: string) =>
    ApiService.post<void>(`/api/adventure-transfers/${transferId}/decline`),

  /** Take an offer back before anyone has answered it. */
  withdraw: (transferId: string) =>
    ApiService.delete<void>(`/api/adventure-transfers/${transferId}`),
}
